import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import {
  NodeFieldLabel,
  NodeTextField,
} from "@/components/doc/right-sidebar/active-node-style/fields/NodeTextField";
import { InlineNodeComponent } from "@/components/expression/InlineNode";
import { NodeStyleProvider } from "@/components/expression/utils/NodeStyleProvider";
import { Icon } from "@/components/icons/Icon";
import { Dropdown, type DropdownItem } from "@/components/sidebar/Dropdown";
import { twMerge } from "@/utils/twMerge";

import type { DocumentModel } from "@/models/DocumentModel";
import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";

export type VariableNodeEntry = {
  name: string;
  node: InlineNodeModel | undefined;
};

type Props = {
  doc: DocumentModel;
  name: string;
  node: InlineNodeModel | null;
  nodes: VariableNodeEntry[];
  isValid: boolean;
  onSelect: (name: string) => void;
};

export const TaylorSeriesVariableField = observer(function TaylorSeriesVariableField({
  doc,
  name,
  node,
  nodes,
  isValid,
  onSelect,
}: Props) {
  const t = useTranslations("sidebar.taylorSeries");

  if (!node) {
    return (
      <NodeTextField
        labelProps={{ label: t("variable") }}
        value={name}
        readOnly
        aria-invalid={!isValid}
        className="cursor-default"
      />
    );
  }

  const hasMultiple = nodes.length > 1;

  const field = (
    <NodeFieldLabel
      as="div"
      label={t("variable")}
      className={twMerge(
        "py-0.5 pr-0.5 min-h-[28px] flex-1 min-w-0 flex-wrap shadow-block dark:shadow-block-inverted cursor-default",
        hasMultiple && "cursor-pointer",
      )}
      labelClassName="py-1 pr-1"
    >
      <div
        className={twMerge(
          "px-1 py-[1px] font-mono overflow-auto no-scrollbar flex items-baseline",
          hasMultiple && "pr-6",
        )}
      >
        <NodeStyleProvider renderContext="right-sidebar" size="sm">
          <InlineNodeComponent doc={doc} nodes={node.nodes} className="pointer-events-none" />
        </NodeStyleProvider>
      </div>
      {hasMultiple && (
        <div className="absolute right-0 top-0 bottom-0 p-0.5 pl-0">
          <Icon
            name="ArrowDownLarge"
            width={16}
            height={16}
            className="h-full mr-1 [&>path]:stroke-document-text"
          />
        </div>
      )}
    </NodeFieldLabel>
  );

  if (!hasMultiple) {
    return field;
  }

  const items: DropdownItem[] = nodes.map(({ name: itemName, node: itemNode }) => ({
    component: ({ close }) => {
      return (
        <button
          type="button"
          onClick={() => {
            onSelect(itemName);
            close();
          }}
          className="w-full min-h-8.5 py-1.25 px-2.5 text-sm flex items-center rounded-8 hover:bg-sidebar-button-active-bg"
        >
          {itemNode ? (
            <NodeStyleProvider renderContext="right-sidebar" size="sm">
              <InlineNodeComponent
                doc={doc}
                nodes={itemNode.nodes}
                className="pointer-events-none font-mono"
              />
            </NodeStyleProvider>
          ) : (
            <span>{itemName}</span>
          )}
          {itemName === name && <Icon name="Check" className="w-6 h-6 ml-auto -mr-1" />}
        </button>
      );
    },
  }));

  return <Dropdown items={items} toggleButtonLabel={t("variable")} toggleButtonComponent={field} />;
});
